import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { buttonClass } from "@/design/buttons";
import { DataUnavailable } from "@/design/DataUnavailable";
import {
  MealPlanBodyEditor,
  toEditorBody,
  toPlanBody,
  type EditorBody,
} from "@/design/MealPlanBodyEditor";
import { MealPlanReader } from "@/design/MealPlanReader";
import type {
  PlanEditorView,
  TemplateEditorView,
  TemplatesView,
} from "@/design/mealPlanViews";
import { Banner, Field, FIELD_INPUT, FIELD_LABEL, PageShell } from "@/design/ui";
import { formStateFor } from "@/modules/meal_plans/commands/mealPlansCopy";
import { TITLE_MAX } from "@/modules/meal_plans/surfaces";
import { BusinessPage } from "~/components/BusinessChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { del, post, put } from "~/lib/api";
import { useForm } from "~/lib/useForm";
import { useQuery } from "~/lib/useQuery";
import { NotFoundScreen } from "~/screens/NotFoundScreen";

/**
 * The clinic's meal-plan screens (R23, H53): the plan editor and the
 * templates. Specified in docs/ux/screens/MealPlanEditorScreen.md and
 * MealPlanTemplatesScreen.md.
 *
 * Open to **both tiers** (H26's second named exception, after the Note): the
 * person who saw the client is the person writing the plan. Every act is
 * re-checked by its command; the permissions in the payload only decide which
 * buttons are worth drawing.
 */

const UNAVAILABLE =
  "Meal plans are available to clinics listed under Nutritionist. You can still read the plans you have already written.";

/** The frame every screen here shares: the way back, then the content. */
function Frame({
  back,
  backLabel,
  children,
}: {
  back: string;
  backLabel: string;
  children: ReactNode;
}) {
  return (
    <BusinessPage>
      <PageShell>
        <div className="mx-auto w-full max-w-2xl">
          <Link
            to={back}
            className="mb-4 inline-block text-sm font-medium text-accent print:hidden"
          >
            ← {backLabel}
          </Link>
          {children}
        </div>
      </PageShell>
    </BusinessPage>
  );
}

/**
 * A consequential act asks once, inline, rather than in a browser dialog: the
 * sentence that explains the consequence sits next to the button that causes
 * it, and nothing modal can be dismissed by a stray Enter.
 */
function ConfirmStep({
  label,
  question,
  confirmLabel,
  pendingLabel,
  variant = "secondary",
  pending,
  onConfirm,
}: {
  label: string;
  question: string;
  confirmLabel: string;
  pendingLabel: string;
  variant?: "primary" | "secondary" | "danger";
  pending: boolean;
  onConfirm: () => void;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button
        type="button"
        disabled={pending}
        onClick={() => setAsking(true)}
        className={buttonClass(variant)}
      >
        {label}
      </button>
    );
  }
  return (
    <div className="w-full rounded-lg border border-line bg-page p-3 text-sm">
      <p className="mb-2 text-ink">{question}</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={pending}
          onClick={onConfirm}
          className={buttonClass(variant === "secondary" ? "primary" : variant)}
        >
          {pending ? pendingLabel : confirmLabel}
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={() => setAsking(false)}
          className={buttonClass("ghost")}
        >
          Not now
        </button>
      </div>
    </div>
  );
}

// ── The plan editor (docs/ux/screens/MealPlanEditorScreen.md) ────────────────

/** `/business/customers/:consumerId/meal-plans/new`, optionally `?template=`. */
export function NewMealPlanScreen() {
  const { consumerId = "" } = useParams();
  const [params] = useSearchParams();
  const template = params.get("template") ?? "";
  const view = useQuery<PlanEditorView>(
    consumerId
      ? `/business/customers/${consumerId}/meal-plans/new${template ? `?template=${encodeURIComponent(template)}` : ""}`
      : null,
  );
  if (view.failure === "not_found") return <NotFoundScreen />;
  return (
    <EditorFrame
      view={view.data}
      loading={view.loading && !view.data}
      failure={view.failure}
      backTo={`/business/customers/${consumerId}`}
      // A different template is a different starting point, so the form is
      // re-seeded rather than merged into what was typed.
      seedKey={`new:${consumerId}:${template}`}
    />
  );
}

/** `/business/meal-plans/:planId`. */
export function MealPlanEditorScreen() {
  const { planId = "" } = useParams();
  const view = useQuery<PlanEditorView>(
    planId ? `/business/meal-plans/${planId}` : null,
  );
  if (view.failure === "meal_plan_not_found") return <NotFoundScreen />;
  const customerId = view.data?.customer.id;
  return (
    <EditorFrame
      view={view.data}
      loading={view.loading && !view.data}
      failure={view.failure}
      backTo={customerId ? `/business/customers/${customerId}` : "/business/customers"}
      seedKey={`plan:${planId}:${view.data?.plan.status ?? ""}`}
      reload={view.reload}
    />
  );
}

function EditorFrame({
  view,
  loading,
  failure,
  backTo,
  seedKey,
  reload,
}: {
  view: PlanEditorView | null;
  loading: boolean;
  failure: string | null;
  backTo: string;
  seedKey: string;
  reload?: () => void;
}) {
  return (
    <Frame back={backTo} backLabel="Back to the customer's file">
      {loading ? (
        <ScreenPending />
      ) : failure || !view ? (
        <DataUnavailable
          configured={failure !== "not_configured"}
          what="this meal plan"
        />
      ) : (
        <PlanEditor key={seedKey} view={view} reload={reload} />
      )}
    </Frame>
  );
}

function PlanEditor({
  view,
  reload,
}: {
  view: PlanEditorView;
  reload?: () => void;
}) {
  const navigate = useNavigate();
  const form = useForm();
  const isNew = view.plan.id === null;
  const editable = isNew ? view.available : view.permissions.canEdit;

  const [title, setTitle] = useState(view.plan.title);
  const [startsOn, setStartsOn] = useState(view.plan.startsOn);
  const [endsOn, setEndsOn] = useState(view.plan.endsOn ?? "");
  const [bookingId, setBookingId] = useState(view.plan.bookingId ?? "");
  const [showMacros, setShowMacros] = useState(view.plan.showMacros);
  const [body, setBody] = useState<EditorBody>(() => toEditorBody(view.plan.body));
  const [notice, setNotice] = useState<string | null>(null);

  const payload = () => ({
    title,
    startsOn,
    endsOn,
    bookingId,
    showMacros,
    body: toPlanBody(body),
  });

  /** Save what is on screen. Resolves to the plan's id, or null on refusal. */
  async function save(): Promise<string | null> {
    setNotice(null);
    if (isNew) {
      const created = await form.submit(
        () =>
          post<{ planId: string }>(
            `/business/customers/${view.customer.id}/meal-plans`,
            payload(),
          ),
        formStateFor,
      );
      return created?.planId ?? null;
    }
    const saved = await form.submit(
      () => put(`/business/meal-plans/${view.plan.id}`, payload()),
      formStateFor,
    );
    return saved ? view.plan.id : null;
  }

  async function onSave(event: FormEvent) {
    event.preventDefault();
    const planId = await save();
    if (!planId) return;
    if (isNew) {
      void navigate(`/business/meal-plans/${planId}`, { replace: true });
      return;
    }
    setNotice(
      view.plan.status === "shared"
        ? `Saved. ${view.customer.name} has been told the plan changed.`
        : "Draft saved.",
    );
    reload?.();
  }

  /** Share always shares what is on screen: it saves first, then shares. */
  async function onShare() {
    const planId = await save();
    if (!planId) return;
    const shared = await form.submit(
      () => post(`/business/meal-plans/${planId}/share`),
      formStateFor,
    );
    if (shared) reload?.();
  }

  async function onArchive() {
    const archived = await form.submit(
      () => post(`/business/meal-plans/${view.plan.id}/archive`),
      formStateFor,
    );
    if (archived) reload?.();
  }

  async function onDelete() {
    const deleted = await form.submit(
      () => del(`/business/meal-plans/${view.plan.id}`),
      formStateFor,
    );
    if (deleted) {
      void navigate(`/business/customers/${view.customer.id}`, { replace: true });
    }
  }

  const errors = form.state.fieldErrors;

  return (
    <>
      <div className="mb-5">
        <p className="text-sm text-muted">Meal plan for {view.customer.name}</p>
        <h1 className="text-xl font-bold text-ink">
          {isNew ? "New meal plan" : view.plan.title}
        </h1>
        {!isNew && (
          <p className="mt-1 text-sm text-muted">
            {view.plan.phaseLabel}
            {view.plan.authorName ? ` · written by ${view.plan.authorName}` : ""}
          </p>
        )}
      </div>

      {!view.available && <Banner tone="info">{UNAVAILABLE}</Banner>}
      {view.plan.status === "shared" && editable && (
        <Banner tone="info">
          {view.customer.name} can see this plan. Saving a change updates it in
          place and tells them it changed.
        </Banner>
      )}
      {view.plan.status === "archived" && (
        <Banner tone="info">
          This plan is archived. {view.customer.name} still sees it, marked no
          longer active, and it can no longer be changed.
        </Banner>
      )}
      {form.state.formError && <Banner tone="danger">{form.state.formError}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      {!editable ? (
        <MealPlanReader body={view.plan.body} />
      ) : (
        <form onSubmit={onSave} noValidate>
          {isNew && view.templates.length > 0 && (
            <TemplatePicker
              templates={view.templates}
              onPick={(templateId) =>
                navigate(
                  `/business/customers/${view.customer.id}/meal-plans/new${templateId ? `?template=${templateId}` : ""}`,
                  { replace: true },
                )
              }
            />
          )}

          <Field
            label="Title"
            name="title"
            value={title}
            onChange={setTitle}
            maxLength={TITLE_MAX}
            placeholder="Two-week reset"
            error={errors?.title}
          />
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Field
              label="Starts on"
              name="startsOn"
              type="date"
              value={startsOn}
              onChange={setStartsOn}
              error={errors?.startsOn}
            />
            <Field
              label="Ends on (optional)"
              name="endsOn"
              type="date"
              value={endsOn}
              onChange={setEndsOn}
              error={errors?.endsOn}
            />
          </div>

          {view.visits.length > 0 && (
            <label className="mb-4 block">
              <span className={FIELD_LABEL}>About which visit? (optional)</span>
              <select
                value={bookingId}
                onChange={(event) => setBookingId(event.target.value)}
                className={FIELD_INPUT}
              >
                <option value="">Not about one visit</option>
                {view.visits.map((visit) => (
                  <option key={visit.id} value={visit.id}>
                    {visit.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <MacrosSwitch
            checked={showMacros}
            onChange={setShowMacros}
            customerName={view.customer.name}
          />

          <MealPlanBodyEditor
            value={body}
            onChange={setBody}
            errors={errors}
            disabled={form.pending}
          />

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={form.pending}
              className={buttonClass(
                !isNew && view.plan.status === "draft" ? "secondary" : "primary",
              )}
            >
              {form.pending
                ? "Saving…"
                : view.plan.status === "shared"
                  ? "Save changes"
                  : "Save draft"}
            </button>
            {/* Not on a new plan: sharing needs a saved plan to share, and a
                create that succeeded followed by a share that did not would
                leave this screen able to create it twice. */}
            {!isNew && view.plan.status === "draft" && view.customer.id && (
              <ConfirmStep
                label="Share with customer"
                question={`Share this plan with ${view.customer.name}? They will see it in Bookr and get a message with a link to it. Once shared, it can be archived but not deleted.`}
                confirmLabel="Share now"
                pendingLabel="Sharing…"
                variant="primary"
                pending={form.pending}
                onConfirm={() => void onShare()}
              />
            )}
          </div>
        </form>
      )}

      {!isNew && (view.permissions.canArchive || view.permissions.canDelete) && (
        <div className="mt-8 border-t border-line pt-5">
          {view.permissions.canArchive && (
            <ConfirmStep
              label="Archive plan"
              question={`Archive this plan? ${view.customer.name} keeps it, marked no longer active, and nobody can change it again. They are not sent a message.`}
              confirmLabel="Archive"
              pendingLabel="Archiving…"
              pending={form.pending}
              onConfirm={() => void onArchive()}
            />
          )}
          {view.permissions.canDelete && (
            <ConfirmStep
              label="Delete draft"
              question="Delete this draft? The customer has never seen it, and it cannot be recovered."
              confirmLabel="Delete"
              pendingLabel="Deleting…"
              variant="danger"
              pending={form.pending}
              onConfirm={() => void onDelete()}
            />
          )}
        </div>
      )}
    </>
  );
}

/**
 * Show macros to the customer (H54), a switch rather than a checkbox because
 * it is a setting of the plan that takes effect at once on save, and the help
 * line says who it is for: a client for whom counting does harm.
 */
function MacrosSwitch({
  checked,
  onChange,
  customerName,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  customerName: string;
}) {
  return (
    <div className="mb-5 flex items-start justify-between gap-4 rounded-xl border border-line bg-surface p-4 shadow-sm">
      <div>
        <p id="macros-switch-label" className="text-sm font-semibold text-ink">
          Show macros to {customerName}
        </p>
        <p className="mt-0.5 text-xs text-muted">
          Turn off for a client who is better off without the numbers. You still
          see them here; {customerName} sees the plan without them.
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby="macros-switch-label"
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${checked ? "bg-accent" : "bg-line"}`}
      >
        <span
          aria-hidden="true"
          className={`inline-block size-5 rounded-full bg-surface shadow-sm transition-transform duration-150 ${checked ? "translate-x-5" : "translate-x-0.5"}`}
        />
      </button>
    </div>
  );
}

function TemplatePicker({
  templates,
  onPick,
}: {
  templates: { id: string; title: string }[];
  onPick: (templateId: string) => void;
}) {
  const [params] = useSearchParams();
  return (
    <label className="mb-5 block rounded-xl border border-line bg-surface p-4 shadow-sm">
      <span className={FIELD_LABEL}>Start from a template</span>
      <select
        value={params.get("template") ?? ""}
        onChange={(event) => onPick(event.target.value)}
        className={FIELD_INPUT}
      >
        <option value="">Blank plan</option>
        {templates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.title}
          </option>
        ))}
      </select>
      <span className="mt-1 block text-xs text-muted">
        The template is copied into this plan. Changing the template later does
        not change this plan. Picking one replaces what is below.
      </span>
    </label>
  );
}

// ── Templates (docs/ux/screens/MealPlanTemplatesScreen.md) ───────────────────

/** `/business/meal-plan-templates`. */
export function MealPlanTemplatesScreen() {
  const view = useQuery<TemplatesView>("/business/meal-plan-templates");
  return (
    <Frame back="/business/customers" backLabel="Back to customers">
      <h1 className="mb-1 text-xl font-bold text-ink">Meal plan templates</h1>
      <p className="mb-5 text-sm text-muted">
        A template is a starting point. Starting a plan from one copies it, so
        editing a template never changes a plan you already gave somebody.
      </p>
      {view.loading && !view.data ? (
        <ScreenPending />
      ) : view.failure || !view.data ? (
        <DataUnavailable
          configured={view.failure !== "not_configured"}
          what="your templates"
        />
      ) : (
        <>
          {!view.data.available && <Banner tone="info">{UNAVAILABLE}</Banner>}
          {view.data.available && (
            <Link
              to="/business/meal-plan-templates/new"
              className={buttonClass("primary", "mb-5")}
            >
              New template
            </Link>
          )}
          {view.data.templates.length === 0 ? (
            <p className="rounded-xl border border-line bg-surface p-5 text-sm text-body shadow-sm">
              No templates yet. A template saves you rewriting the plan you give
              most often.
            </p>
          ) : (
            <ul className="space-y-3">
              {view.data.templates.map((template) => (
                <li key={template.id}>
                  <Link
                    to={`/business/meal-plan-templates/${template.id}`}
                    className="block rounded-xl border border-line bg-surface p-4 shadow-sm transition-colors hover:border-accent"
                  >
                    <span className="font-semibold text-ink">{template.title}</span>
                    <span className="mt-0.5 block text-sm text-muted">
                      Last changed {template.updatedOn}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Frame>
  );
}

/** `/business/meal-plan-templates/:templateId`, where `new` is a blank one. */
export function MealPlanTemplateEditorScreen() {
  const { templateId = "new" } = useParams();
  const view = useQuery<TemplateEditorView>(
    `/business/meal-plan-templates/${templateId}`,
  );
  if (view.failure === "meal_plan_template_not_found") return <NotFoundScreen />;
  return (
    <Frame back="/business/meal-plan-templates" backLabel="Back to templates">
      {view.loading && !view.data ? (
        <ScreenPending />
      ) : view.failure || !view.data ? (
        <DataUnavailable
          configured={view.failure !== "not_configured"}
          what="this template"
        />
      ) : (
        <TemplateEditor key={templateId} view={view.data} reload={view.reload} />
      )}
    </Frame>
  );
}

function TemplateEditor({
  view,
  reload,
}: {
  view: TemplateEditorView;
  reload: () => void;
}) {
  const navigate = useNavigate();
  const form = useForm();
  const isNew = view.template.id === null;
  const [title, setTitle] = useState(view.template.title);
  const [body, setBody] = useState<EditorBody>(() =>
    toEditorBody(view.template.body),
  );
  const [notice, setNotice] = useState<string | null>(null);

  async function onSave(event: FormEvent) {
    event.preventDefault();
    setNotice(null);
    const payload = { title, body: toPlanBody(body) };
    if (isNew) {
      const created = await form.submit(
        () =>
          post<{ templateId: string }>("/business/meal-plan-templates", payload),
        formStateFor,
      );
      if (created) {
        void navigate(`/business/meal-plan-templates/${created.templateId}`, {
          replace: true,
        });
      }
      return;
    }
    const saved = await form.submit(
      () => put(`/business/meal-plan-templates/${view.template.id}`, payload),
      formStateFor,
    );
    if (saved) {
      setNotice("Template saved. Plans already started from it are unchanged.");
      reload();
    }
  }

  async function onDelete() {
    const deleted = await form.submit(
      () => del(`/business/meal-plan-templates/${view.template.id}`),
      formStateFor,
    );
    if (deleted) void navigate("/business/meal-plan-templates", { replace: true });
  }

  return (
    <>
      <h1 className="mb-5 text-xl font-bold text-ink">
        {isNew ? "New template" : view.template.title}
      </h1>
      {!view.available && <Banner tone="info">{UNAVAILABLE}</Banner>}
      {form.state.formError && <Banner tone="danger">{form.state.formError}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}

      {view.available ? (
        <form onSubmit={onSave} noValidate>
          <Field
            label="Title"
            name="title"
            value={title}
            onChange={setTitle}
            maxLength={TITLE_MAX}
            placeholder="Low-sodium week"
            error={form.state.fieldErrors?.title}
          />
          <MealPlanBodyEditor
            value={body}
            onChange={setBody}
            errors={form.state.fieldErrors}
            disabled={form.pending}
          />
          <button
            type="submit"
            disabled={form.pending}
            className={buttonClass("primary", "mt-6")}
          >
            {form.pending ? "Saving…" : "Save template"}
          </button>
        </form>
      ) : (
        <MealPlanReader body={view.template.body} />
      )}

      {!isNew && (
        <div className="mt-8 border-t border-line pt-5">
          <ConfirmStep
            label="Delete template"
            question="Delete this template? Plans already started from it are not affected."
            confirmLabel="Delete"
            pendingLabel="Deleting…"
            variant="danger"
            pending={form.pending}
            onConfirm={() => void onDelete()}
          />
        </div>
      )}
    </>
  );
}
